import { DOCUMENT } from '@angular/common';
import {
    ApplicationRef,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { POP_DIRECTION } from '@components/pop/pop-direction';
import { WindowRef } from '@vendor/window/window.component';
import {
    Subject,
    combineLatest,
    debounceTime,
    delay,
    filter,
    fromEvent,
    map,
    merge,
    takeUntil
} from 'rxjs';
import { gsap } from 'gsap';
import { CSSPlugin } from '@vendor/gsap';
import { Sine } from 'gsap/all';

gsap.registerPlugin(CSSPlugin);

@Component({
    selector: 'ihd-tooltip',
    templateUrl: './tooltip.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'c-tooltip',
        '[class.c-tooltip__top]': 'direction === POP_DIRECTION.TOP',
        '[class.c-tooltip__bottom]': 'direction === POP_DIRECTION.BOTTOM',
        '[class.is-active]': 'isVisible',
    },
})
export class TooltipComponent implements OnInit, OnDestroy {
    @Input() name: string;
    @Input('is-visible') isVisible: boolean = false;
    @Input() direction: string = 'bottom';
    @Input() delay: number = 0;
    @Input() autoHide: boolean = false;
    @Input('custom-class') customClass: string = '';

    @ViewChild('tooltip') public tooltipElement: ElementRef;

    /**
     * Triggers a blur event on the tooltips parent element to force a tooltip hide.
     * @param event
     */
    public static hideTooltip(event: any) {
        let btnElement = event.target;
        if (event.target.nodeName === 'I' || event.target.nodeName === 'SPAN') {
            btnElement = event.target.parentNode;
        }
        if (event.target.nodeName === 'svg') {
            btnElement = event.target.parentNode.parentNode;
        }

        if (btnElement) {
            btnElement.blur();
        }
    }

    public static tooltips$: Subject<any> = new Subject<any>();

    public animationState: string = 'initial';

    public readonly POP_DIRECTION = POP_DIRECTION;
    private readonly TOOLTIP_SHOW_DELAY = 0;
    private readonly TOOLTIP_WINDOW_EDGE_SPACE = 8;
    private nativeWindow: Window;
    public visible: any = {
        queued: false,
        value: false,
    };
    private nativeElement: Node;
    private parent: Node;
    private parentNativeElement: HTMLElement;

    private content;
    private current;
    private tooltipParent;
    private timer: any;
    private destroyed$: Subject<void> = new Subject();
    private defaultDuration: number = 0.1;

    constructor(
        @Inject(DOCUMENT) private document: any,
        private cdr: ChangeDetectorRef,
        private appRef: ApplicationRef,
        private element: ElementRef,
        private renderer: Renderer2,
        windowRef: WindowRef
    ) {
        this.nativeElement = element.nativeElement;
        this.nativeWindow = windowRef.nativeWindow;
        this.delay = this.TOOLTIP_SHOW_DELAY;

        // Hide triggered by other tooltips
        TooltipComponent.tooltips$
            .pipe(takeUntil(this.destroyed$))
            .subscribe(({ activeElement }) => {
                if (activeElement !== this.element) {
                    this.hideToolTip();
                }
            });
    }

    /**
     * Initialises tooltip component
     *
     * @memberof TooltipComponent
     */
    ngOnInit() {
        this.parent = this.renderer.parentNode(this.nativeElement);
        this.parentNativeElement = this.nativeElement.parentElement;
        this.content = this.nativeElement.firstChild;
        this.current = this.getNearestContentElement();
        this.tooltipParent = this.current || this.document.body;

        this.manipulateElement();
        this.bindEvents();
        gsap.set(this.content, {
            'transform-origin': this.getTransformOrigin(this.direction),
        });
    }

    ngOnDestroy() {
        this.isVisible = false;
        this.cdr.markForCheck();
        gsap.killTweensOf(this.tooltipElement.nativeElement);
        this.destroyed$.next();

        if (this.nativeElement.parentNode) {
            this.nativeElement.parentNode.removeChild(this.nativeElement);
        }
    }

    /**
     * Shows tooltip
     *
     * @returns
     * @memberof TooltipComponent
     */
    showToolTip() {
        // clearTimeout(this.timer);
        this.animationState = 'hidden';
        this.cdr.markForCheck();
        this.renderer.appendChild(this.tooltipParent, this.nativeElement);

        gsap.killTweensOf(this.tooltipElement.nativeElement);
        gsap.set(this.tooltipElement.nativeElement, {
            css: { opacity: 0 },
        });
        gsap.to(this.tooltipElement.nativeElement, {
            duration: this.defaultDuration,
            delay: 0.5,
            css: {
                scale: 1,
                opacity: 1,
            },
            ease: Sine.easeOut,
            onComplete: () => {
                // Tooltip is shown
                this.animationState = 'shown';
                this.cdr.markForCheck();
                this.appRef.tick();
            },
        });

        TooltipComponent.tooltips$.next({ activeElement: this.element });
        this.positionTooltip();
    }

    update() {
        this.positionTooltip();
        this.cdr.markForCheck();
        this.appRef.tick();
    }

    hideToolTip() {
        // clearTimeout(this.timer);
        gsap.killTweensOf(this.tooltipElement.nativeElement);
        // if (this.isVisible) {
        // if (this.animationState !== 'hidden') {
        // [this.nativeElement, this.content].forEach((x) => {
        //     if (x.parentElement && x.classList.contains('md-show')) {
        //         this.parentNativeElement.blur();
        //         this.renderer.removeClass(x, 'md-show');
        //     }
        // });

        this.animationState = 'hidden';
        //  }

        gsap.to(this.tooltipElement.nativeElement, {
            duration: this.defaultDuration,
            css: {
                opacity: 0,
            },
            ease: Sine.easeOut,
        });

        this.visible.value = false;
        this.isVisible = false;
        this.cdr.markForCheck();
    }

    private getOffsetRect(element: any, offsetParent: any): any {
        let node = element.nativeElement || element;
        let isOffsetRect = true;
        offsetParent = offsetParent || element.offsetParent || document.body;

        let nodeRect = node.getBoundingClientRect();

        // The user can ask for an offsetRect: a rect relative to the offsetParent,
        // or a clientRect: a rect relative to the page
        let offsetRect = isOffsetRect
            ? offsetParent.getBoundingClientRect()
            : { left: 0, top: 0, width: 0, height: 0 };
        return {
            left: nodeRect.left - offsetRect.left,
            top: nodeRect.top - offsetRect.top,
            width: nodeRect.width,
            height: nodeRect.height,
        };
    }

    private getTransformOrigin(direction: string): string {
        let result: string;

        switch (direction) {
            case 'left':
                result = 'right center';
                break;

            case 'right':
                result = 'left center';
                break;

            case 'top':
                result = 'center bottom';
                break;

            case 'bottom':
                result = 'center top';
                break;

            default:
                result = 'right center';
                break;
        }

        return result;
    }

    private manipulateElement() {
        this.renderer.removeChild(this.parent, this.nativeElement);
        this.renderer.setAttribute(this.nativeElement, 'role', 'tooltip');
    }

    /**
     * Scan up dom hierarchy for enabled parent;
     */
    private getParentWithPointerEvents(): HTMLElement {
        let parent = this.nativeElement.parentElement;
        return parent;
    }

    private getNearestContentElement(): Node {
        let current = this.getParentWithPointerEvents();

        // Look for the nearest parent md-content, stopping at the rootElement.
        while (
            current &&
            current !== document.body &&
            current.style?.pointerEvents !== 'none'
        ) {
            current = current.parentElement;
        }
        return current;
    }

    /**
     * Configure observables to support tooltip behavior
     *
     * @memberof TooltipComponent
     */
    private bindEvents() {
        let mouseActive = false;
        let win = this.nativeWindow;
        let parent = this.parent;
        let visible = this.visible;
        let inputVisible = this.isVisible;
        let autoHide = this.autoHide;
        let elementFocusedOnWindowBlur = false;

        // Store whether the element was focused when the window loses focus.
        fromEvent(win, 'blur')
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                elementFocusedOnWindowBlur = document.activeElement === parent;
            });


        // Create observables from events that will hide the tooltip and merge them into one
        let tooltipDeactivates = merge(
            fromEvent(parent, 'blur'),
            fromEvent(parent, 'mouseleave'),
            fromEvent(parent, 'touchend'),
            fromEvent(parent, 'touchcancel'),
            fromEvent(this.parentNativeElement, 'mousedown')
        );

        // to avoid `synthetic clicks` we listen to mousedown instead of `click`
        fromEvent(parent, 'mousedown')
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                mouseActive = true;
            });

        // Create observables from events that will trigger the tooltip and merge them into one
        let tooltipActivates = merge(
            fromEvent(parent, 'mouseenter'),
            fromEvent(parent, 'focus')
        )

        // Position tooltip again if window resizes
        fromEvent(win, 'resize')
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                if (this.visible.value === true) {
                    this.positionTooltip();
                }
            });

        // Subscribe actions to events that deactivate the tooltip
        tooltipDeactivates.pipe(delay(0)).subscribe(() => {
            autoHide = autoHide || true;
            if (autoHide || mouseActive || document.activeElement !== parent) {
                this.hideToolTip();
            }

            mouseActive = false;
        });

        let entered = merge(
            tooltipActivates.pipe(map((e) => true)),
            tooltipDeactivates.pipe(map((e) => false))
        )
        .pipe(takeUntil(this.destroyed$))

        // Subscribe 'show Tooltip' to actions that trigger it. debounce if there is any delay assigned to the component
        combineLatest([
            tooltipActivates.pipe(
                takeUntil(this.destroyed$),
                debounceTime(this.delay)
            ),
            entered
        ])
        .pipe(
            filter(([e, b]) => !!b),
            map(([e, _]) => e)
        )
        .subscribe((event: MouseEvent) => {
            // Prevent the tooltip from showing when the window is receiving focus.
            if (!this.isVisible) {
                if (event.type === 'focus' && elementFocusedOnWindowBlur) {
                    elementFocusedOnWindowBlur = false;
                } else {
                    // Otherwise show
                    this.visible.value = true;
                    this.isVisible = visible.value;
                    this.visible.queued = false;
                    this.showToolTip();
                }
            }
        });
    }

    private fitInParent(pos: any, tipRect: any): any {
        let newPosition = { left: pos.left, top: pos.top };
        newPosition.left = Math.min(
            newPosition.left,
            this.tooltipParent.scrollWidth  -
                (tipRect.width/2) +
                this.TOOLTIP_WINDOW_EDGE_SPACE
        );
        newPosition.left = Math.max(
            newPosition.left,
            this.TOOLTIP_WINDOW_EDGE_SPACE
        );

        // Note: This below needs to be reviewed
        // newPosition.top = Math.min(
        //     newPosition.top,
        //     this.tooltipParent.height -
        //         tipRect.height -
        //         this.TOOLTIP_WINDOW_EDGE_SPACE
        // );

        //     console.log('min 1 => ', newPosition.top);
        //     console.log('min 2 => ', this.tooltipParent.scrollHeight -
        //     tipRect.height -
        //     this.TOOLTIP_WINDOW_EDGE_SPACE);


        newPosition.top = Math.max(
            newPosition.top,
            this.TOOLTIP_WINDOW_EDGE_SPACE
        );
        return newPosition;
    }

    /**
     * Determines tooltip position
     *
     * @memberof TooltipComponent
     */
    private getPosition(
        dir: string,
        parentRect: any,
        tipRect: any,
        tooltipEdgeSpace: number
    ): any {
        return dir === 'left'
            ? {
                  left: parentRect.left - tipRect.width - tooltipEdgeSpace,
                  top:
                      parentRect.top +
                      parentRect.height / 2 -
                      tipRect.height / 2,
              }
            : dir === 'right'
            ? {
                  left: parentRect.left + parentRect.width + tooltipEdgeSpace,
                  top:
                      parentRect.top +
                      parentRect.height / 2 -
                      tipRect.height / 2,
              }
            : dir === 'top'
            ? {
                  left:
                      parentRect.left +
                      parentRect.width / 2 -
                      tipRect.width / 2,
                  top: parentRect.top - tipRect.height - tooltipEdgeSpace,
              }
            : {
                  left:
                      parentRect.left +
                      parentRect.width / 2 -
                      tipRect.width / 2,
                  top: parentRect.top + parentRect.height + tooltipEdgeSpace,
              };
    }

    /**
     * Applies new tooltip position
     *
     * @memberof TooltipComponent
     */
    private positionTooltip() {
        let tipRect = this.getOffsetRect(this.element, this.tooltipParent);
        let parentRect = this.getOffsetRect(this.parent, this.tooltipParent);

        let newPosition = this.getPosition(
            this.direction,
            parentRect,
            tipRect,
            this.TOOLTIP_WINDOW_EDGE_SPACE
        );

        // If the user provided a direction, just nudge the tooltip onto the screen
        // Otherwise, recalculate based on 'top' since default is 'bottom'
        if (this.direction) {
            newPosition = this.fitInParent(newPosition, tipRect);

        } else if (
            newPosition.top >
            this.element.nativeElement.offsetParent.scrollHeight -
                tipRect.height -
                this.TOOLTIP_WINDOW_EDGE_SPACE
        ) {

            newPosition = this.fitInParent(
                this.getPosition(
                    'top',
                    parentRect,
                    tipRect,
                    this.TOOLTIP_WINDOW_EDGE_SPACE
                ),
                tipRect
            );
        }

        gsap.set(this.nativeElement, {
            top: newPosition.top,
            left: newPosition.left,
        });
    }
}